import { Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { __GetClientRoleCode } from "../../../../../../../common/CommonFuctions";
import { enum__AppElementType } from "../../../../../../../common/Dictionaries";

const ROLE_WIDTHS = {
  AEC: 20,
  ATO: 16,
  AAD: 15,
  ADN: 22,
  RFA1: 9,
  RFA2: 9,
  RFA3: 9,
};

const SECTION_WIDTHS = {
  PREPARACION: 20,
  REVISION: 31,
  APROBACION: 49,
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    fontSize: 8,
  },
  table: {
    border: "1pt solid #000",
  },
  row: {
    flexDirection: "row",
  },

  // Encabezados superiores
  sectionTitle: {
    borderRight: "1pt solid #000",
    borderBottom: "1pt solid #000",
    textAlign: "center",
    fontWeight: "bold",
    paddingVertical: 2,
    paddingTop: 10,
    backgroundColor: "#d0d0d0",
  },
  sectionTitleLast: {
    borderBottom: "1pt solid #000",
    textAlign: "center",
    fontWeight: "bold",
    paddingVertical: 2,
    paddingTop: 10,
    backgroundColor: "#d0d0d0",
  },

  // Encabezados de rol
  roleCell: {
    borderRight: "1pt solid #000",
    borderBottom: "1pt solid #000",
    textAlign: "center",
    fontWeight: "bold",
    paddingVertical: 4,
    backgroundColor: "#f0f0f0",
  },
  roleCellLast: {
    borderBottom: "1pt solid #000",
    textAlign: "center",
    fontWeight: "bold",
    paddingVertical: 4,
    backgroundColor: "#f0f0f0",
  },

  roleBox: {
    borderRight: "1pt solid #000",
  },
  roleBoxLast: {},

  // Cuerpo por rol
  infoLine: {
    flexDirection: "row",
    borderBottom: "1pt solid #000",
    height: 15,
  },
  infoLineLast: {
    flexDirection: "row",
    height: 56, // bloque "Firma"
  },
  labelCell: {
    width: "30%",
    borderRight: "1pt solid #000",
    paddingHorizontal: 2,
    paddingTop: 2,
    fontWeight: "bold",
    fontSize: 7,
  },
  valueCell: {
    width: "70%",
    paddingHorizontal: 2,
    paddingTop: 2,
    fontSize: 7,
  },
  valueCellNoLabel: {
    width: "70%",
    paddingHorizontal: 2,
    paddingTop: 2,
    fontSize: 7,
  },

  // Bloque inferior
  bottomTable: {
    marginTop: 10,
    width: "46%",
    border: "1pt solid #000",
  },
  bottomRow: {
    flexDirection: "row",
  },
  bottomLeft: {
    width: "85%",
    borderRight: "1pt solid #000",
    borderBottom: "1pt solid #000",
    fontWeight: "bold",
    paddingVertical: 2,
    paddingHorizontal: 3,
  },
  bottomRight: {
    width: "15%",
    borderBottom: "1pt solid #000",
    fontWeight: "bold",
    textAlign: "center",
    paddingVertical: 2,
    paddingHorizontal: 2,
  },
  bottomLeftLast: {
    width: "85%",
    borderRight: "1pt solid #000",
    fontWeight: "bold",
    paddingVertical: 2,
    paddingHorizontal: 3,
  },
  bottomRightLast: {
    width: "15%",
    textAlign: "center",
    paddingVertical: 2,
    paddingHorizontal: 2,
    fontWeight: "bold",
  },
  firmaImageWrap: {
    paddingHorizontal: 2,
    paddingVertical: 2,
  },
  firmaImage: {
    maxWidth: "95%",
    maxHeight: 48,
    objectFit: "contain",
  },
});

const LABEL_ROLES = new Set(["AEC", "ATO", "AAD", "ADN"]);

const normalizeSignatureSrc = (firma) => {
  if (!firma || typeof firma !== "string") return "";
  if (firma.startsWith("data:image")) return firma;
  return `data:image/png;base64,${firma}`;
};

const ROLE_COLUMNS = [
  { key: "AEC", width: `${ROLE_WIDTHS.AEC}%` },
  { key: "ATO", width: `${ROLE_WIDTHS.ATO}%` },
  { key: "AAD", width: `${ROLE_WIDTHS.AAD}%` },
  { key: "ADN", width: `${ROLE_WIDTHS.ADN}%` },
  { key: "RFA1", width: `${ROLE_WIDTHS.RFA1}%` },
  { key: "RFA2", width: `${ROLE_WIDTHS.RFA2}%` },
  { key: "RFA3", width: `${ROLE_WIDTHS.RFA3}%` },
];

// Mapeo de roles a triplet (tipo_respuesta_id, etapa_flujo_id, agrupacion)
const ROLE_TRIPLET = {
  AEC: [1, 1, 1],
  ATO: [2, 4, 1],
  AAD: [3, 4, 1],
  ADN: [4, 5, 1],
  RFA1: [5, 6, 2],
  RFA2: [6, 7, 3],
  RFA3: [7, 8, 4],
};

const formatDateYMD = (value) => {
  if (!value) return "";
  if (typeof value === "string" && value.includes("T"))
    return value.split("T")[0];

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getStageByTriplet = (stages, tipo, flujo, agrupacion) => {
  const candidates = (stages ?? []).filter(
    (stage) =>
      Number(stage?.tipo_respuesta_id) === Number(tipo) &&
      Number(stage?.etapa_flujo_id) === Number(flujo) &&
      Number(stage?.agrupacion) === Number(agrupacion),
  );
  // prioriza el que trae item_respuesta
  return (
    candidates.find((stage) => stage?.item_respuesta) ?? candidates[0] ?? null
  );
};

const buildRoleData = (headersData, roleCode) => {
  const stages = headersData?.l_etapas_revision ?? [];
  const triplet = ROLE_TRIPLET[roleCode];

  if (!triplet) {
    return {
      nombre: "",
      rut: "",
      cargo: "",
      fecha: "",
      firma: "",
      displayName: roleCode,
    };
  }

  const [tipo, flujo, agrupacion] = triplet;
  const stage = getStageByTriplet(stages, tipo, flujo, agrupacion);
  const item = stage?.item_respuesta ?? {};

  return {
    nombre: item?.fullname ?? "",
    rut: item?.username ?? "",
    cargo: item?.job ?? "",
    fecha: formatDateYMD(item?.fecha),
    firma: stage?.firmaStr ?? item?.firmaStr ?? "",
    displayName: stage?.apellido || stage?.nombre || roleCode,
  };
};

const printable = (value) => {
  const text = String(value ?? "").trim();
  return text.length ? text : "";
};

const renderFirmaLine = (firmaSrc, showLabels) => (
  <View style={styles.infoLineLast}>
    {showLabels ? <Text style={styles.labelCell}>Firma</Text> : null}
    <View style={showLabels ? styles.valueCell : styles.valueCellNoLabel}>
      {firmaSrc ? (
        <View style={styles.firmaImageWrap}>
          <Image style={styles.firmaImage} src={firmaSrc} />
        </View>
      ) : null}
    </View>
  </View>
);

const renderFirmaStrLine = (firmaSrc, showLabels) => (
  <View style={styles.infoLineLast}>
    {showLabels ? <Text style={styles.labelCell}>Firma</Text> : null}
    <View style={showLabels ? styles.valueCell : styles.valueCellNoLabel}>
      {firmaSrc ? (
        <Text style={styles.firmaImageWrap}>
          {firmaSrc}
        </Text>
      ) : null}
    </View>
  </View>
);

const renderRoleLine = (label, value, showLabels, isLast = false) => (
  <View style={isLast ? styles.infoLineLast : styles.infoLine}>
    {showLabels ? <Text style={styles.labelCell}>{label}</Text> : null}
    <Text style={showLabels ? styles.valueCell : styles.valueCellNoLabel}>
      {printable(value)}
    </Text>
  </View>
);

const getCondicionEp = (headersData) => {
  const statusMap = {
    1: "Borrador",
    2: "En revisión",
    3: "Aprobado",
    4: "Rechazado",
    5: "Anulado",
  };
  return statusMap[headersData?.estadO_ID] ?? "";
};

function DocumentFooter({ headers, applicationRoles }) {
  const data = headers?.data ?? headers ?? {};

  const roleData = ROLE_COLUMNS.reduce((accumulator, role) => {
    accumulator[role.key] = buildRoleData(data, role.key);
    return accumulator;
  }, {});

  return (
    <View style={styles.container} wrap={false}>
      <View style={styles.table}>
        <View style={styles.row}>
          <Text
            style={[
              styles.sectionTitle,
              { width: `${SECTION_WIDTHS.PREPARACION}%` },
            ]}
          >
            Preparación
          </Text>
          <Text
            style={[
              styles.sectionTitle,
              { width: `${SECTION_WIDTHS.REVISION}%` },
            ]}
          >
            Revisión
          </Text>
          <Text
            style={[
              styles.sectionTitleLast,
              { width: `${SECTION_WIDTHS.APROBACION}%` },
            ]}
          >
            Aprobación
          </Text>
        </View>

        <View style={styles.row}>
          {ROLE_COLUMNS.map((role) => {
            const isLast =
              ROLE_COLUMNS[ROLE_COLUMNS.length - 1].key === role.key;
            const displayName = __GetClientRoleCode(applicationRoles, enum__AppElementType.CONTRACT, roleData[role.key]?.displayName || role.key) ;

            return (
              <Text
                key={role.key}
                style={[
                  isLast ? styles.roleCellLast : styles.roleCell,
                  { width: role.width },
                ]}
              >
                {displayName}
              </Text>
            );
          })}
        </View>

        <View style={styles.row}>
          {ROLE_COLUMNS.map((role, index) => {
            const isLast = index === ROLE_COLUMNS.length - 1;
            const values = roleData[role.key] ?? {};
            const showLabels = LABEL_ROLES.has(role.key);

            return (
              <View
                key={role.key}
                style={[
                  { width: role.width },
                  isLast ? styles.roleBoxLast : styles.roleBox,
                ]}
              >
                {renderRoleLine("Nombre", values.nombre, showLabels)}
                {renderRoleLine("Rut", values.rut, showLabels)}
                {renderRoleLine("Cargo", values.cargo, showLabels)}
                {renderRoleLine("Fecha", values.fecha, showLabels)}
                {renderFirmaStrLine(values.firma, showLabels)}
              </View>
            );
          })}
        </View>
      </View>

      <View style={styles.bottomTable}>
        <View style={[styles.bottomRow, { backgroundColor: "#d0d0d0" }]}>
          <Text style={styles.bottomLeft}>Obligaciones</Text>
          <Text style={styles.bottomRight}>Estado</Text>
        </View>
        <View style={styles.bottomRow}>
          <Text style={styles.bottomLeftLast}>Condición EP</Text>
          <Text style={styles.bottomRightLast}>{getCondicionEp(data)}</Text>
        </View>
      </View>
    </View>
  );
}

export default DocumentFooter;
